import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Logo } from '../components/brand/Logo';
import { useAuth } from '../auth/AuthContext';
import * as authApi from '../api/auth';
import { logSignup, logSignupAttempt, logEvent } from '../utils/analytics';

// ── Shared UI helpers ────────────────────────────────────────────────────────

const inputCls = 'h-12 rounded-xl border border-white/15 bg-white/5 px-4 text-white placeholder:text-slate-400 outline-none transition focus:border-primary/70 focus:bg-white/10';

function SubmitButton({ loading, children }) {
  return (
    <button disabled={loading} className="mt-2 h-12 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-6 font-bold text-white shadow-lg shadow-blue-600/30 transition hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed">
      {loading ? 'Please wait…' : children}
    </button>
  );
}

function ErrorBanner({ children }) {
  return <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{children}</div>;
}

function SuccessBanner({ children }) {
  return <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">{children}</div>;
}

function StepDot({ active, done, label }) {
  return (
    <div className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-xs font-black transition-colors ${done ? 'bg-primary text-white' : active ? 'border-2 border-primary text-primary' : 'border-2 border-white/20 text-slate-500'}`}>
      {done ? '✓' : label}
    </div>
  );
}

// ── Login: Step 1 ─────────────────────────────────────────────────────────────
function LoginCredentialsForm({ onOTPSent }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.login({ email, password });
      onOTPSent(email);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="mt-8 flex flex-col gap-5" onSubmit={handleSubmit}>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <label className="flex flex-col gap-2">
        <span className="text-sm font-bold text-slate-200">Email</span>
        <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" required />
      </label>
      <label className="flex flex-col gap-2">
        <span className="text-sm font-bold text-slate-200">Password</span>
        <input className={inputCls} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" required />
      </label>
      <SubmitButton loading={loading}>Sign in</SubmitButton>
      <div className="mt-1 rounded-xl border border-white/10 bg-white/5 p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">What happens next</p>
        <p className="mt-2 text-sm text-slate-300">We'll send a one-time code to your email to confirm it's you.</p>
      </div>
    </form>
  );
}

// ── OTP verification (shared by login + signup) ───────────────────────────────
function OTPForm({ email, onSuccess }) {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { token, user } = await authApi.verifyOTP({ email, otp });
      onSuccess({ token, user });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="mt-8 flex flex-col gap-5" onSubmit={handleSubmit}>
      <div className="rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-blue-200">
        A 6-digit code was sent to <span className="font-bold">{email}</span>
      </div>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <label className="flex flex-col gap-2">
        <span className="text-sm font-bold text-slate-200">Verification code</span>
        <input
          className={`${inputCls} text-center text-2xl font-black tracking-[0.4em]`}
          type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
          value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
          placeholder="000000" autoFocus required
        />
      </label>
      <SubmitButton loading={loading}>Verify &amp; continue</SubmitButton>
    </form>
  );
}

// ── Signup: Step 1 ────────────────────────────────────────────────────────────
function SignupForm({ onOTPSent }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8)  { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await authApi.signup({ name, email, password });
      logSignupAttempt({ email, name });
      onOTPSent(email);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="mt-8 flex flex-col gap-4" onSubmit={handleSubmit}>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <label className="flex flex-col gap-2">
        <span className="text-sm font-bold text-slate-200">Full name</span>
        <input className={inputCls} type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" autoComplete="name" required />
      </label>
      <label className="flex flex-col gap-2">
        <span className="text-sm font-bold text-slate-200">Work email</span>
        <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" required />
      </label>
      <label className="flex flex-col gap-2">
        <span className="text-sm font-bold text-slate-200">Password</span>
        <input className={inputCls} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 8 characters" autoComplete="new-password" required />
      </label>
      <label className="flex flex-col gap-2">
        <span className="text-sm font-bold text-slate-200">Confirm password</span>
        <input className={inputCls} type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat password" autoComplete="new-password" required />
      </label>
      <SubmitButton loading={loading}>Create account</SubmitButton>
      <p className="text-center text-xs text-slate-500">
        By signing up you agree to our{' '}
        <Link to="/terms" className="text-primary hover:underline">Terms</Link>{' '}and{' '}
        <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
      </p>
    </form>
  );
}

// ── Forgot password flow ──────────────────────────────────────────────────────
function ForgotPasswordFlow({ onBack }) {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const sendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.forgotPassword({ email });
      setStep('otp');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetPass = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8)  { setError('Min. 8 characters required'); return; }
    setLoading(true);
    try {
      await authApi.resetPassword({ email, otp, newPassword: password });
      setStep('done');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (step === 'done') return (
    <div className="mt-8 flex flex-col gap-5">
      <SuccessBanner>Password reset successfully. You can now sign in.</SuccessBanner>
      <button onClick={onBack} className="mt-2 h-12 rounded-xl bg-gradient-to-r from-primary to-blue-500 font-bold text-white transition hover:brightness-110">Back to sign in</button>
    </div>
  );

  if (step === 'otp') return (
    <form className="mt-8 flex flex-col gap-5" onSubmit={resetPass}>
      <div className="rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-blue-200">Reset code sent to <span className="font-bold">{email}</span></div>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <label className="flex flex-col gap-2">
        <span className="text-sm font-bold text-slate-200">Reset code</span>
        <input className={`${inputCls} text-center text-2xl font-black tracking-[0.4em]`} type="text" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} placeholder="000000" autoFocus required />
      </label>
      <label className="flex flex-col gap-2">
        <span className="text-sm font-bold text-slate-200">New password</span>
        <input className={inputCls} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 8 characters" required />
      </label>
      <label className="flex flex-col gap-2">
        <span className="text-sm font-bold text-slate-200">Confirm password</span>
        <input className={inputCls} type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat password" required />
      </label>
      <SubmitButton loading={loading}>Reset password</SubmitButton>
    </form>
  );

  return (
    <form className="mt-8 flex flex-col gap-5" onSubmit={sendOtp}>
      <p className="text-sm text-slate-300">Enter your email and we'll send a one-time reset code.</p>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <label className="flex flex-col gap-2">
        <span className="text-sm font-bold text-slate-200">Email</span>
        <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" required />
      </label>
      <SubmitButton loading={loading}>Send reset code</SubmitButton>
      <button type="button" onClick={onBack} className="text-sm text-slate-400 hover:text-white transition">← Back to sign in</button>
    </form>
  );
}

// ── Main AuthPage ─────────────────────────────────────────────────────────────
export function AuthPage() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const defaultMode = searchParams.get('mode') === 'signup' ? 'signup' : 'login';
  const [mode,  setMode]  = useState(defaultMode);
  const [step,  setStep]  = useState('credentials');
  const [email, setEmail] = useState('');

  if (isAuthenticated) return <Navigate to="/console" replace />;

  const handleOTPSent = (e) => { setEmail(e); setStep('otp'); };
  const handleSuccess = ({ token, user }) => {
    if (isSignup) {
      logSignup({ email: user.email, name: user.name });
    } else {
      logEvent({ name: 'login', type: 'auth', userEmail: user.email });
    }
    login({ token, user });
    const redirect = searchParams.get('redirect');
    navigate(redirect ? decodeURIComponent(redirect) : '/console', { replace: true });
  };
  const switchMode    = (m) => { setMode(m); setStep('credentials'); setEmail(''); };

  const isLogin  = mode === 'login';
  const isSignup = mode === 'signup';
  const isForgot = mode === 'forgot';

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#060b1a] text-white">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="pointer-events-none absolute -top-28 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-primary/35 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-[-120px] h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />
      <div className="pointer-events-none absolute right-[-120px] top-[40%] h-72 w-72 rounded-full bg-violet-500/20 blur-3xl" />

      <div className="relative mx-auto flex max-w-[980px] flex-col gap-8 px-6 py-12 md:py-16">
        <Link to="/" className="inline-flex w-fit items-center gap-0.5 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-white/95 backdrop-blur-sm transition hover:bg-white/10">
          <Logo className="h-6 w-auto" />
          <span className="translate-y-0.5 text-sm font-black tracking-wide">euteuristix</span>
        </Link>

        <div className="grid items-stretch gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          {/* Left panel */}
          <div className="relative hidden h-full overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/10 via-white/5 to-transparent p-10 backdrop-blur-md lg:flex lg:flex-col">
            <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1600&q=80" alt="Logistics warehouse" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-35" />
            <div className="pointer-events-none absolute inset-0 bg-slate-950/65" />
            <div className="relative z-10 my-auto">
              <h2 className="text-4xl font-black leading-tight text-white md:text-5xl">
                The cheapest container mix,<br />packed to maximum density.
              </h2>
              <p className="mt-6 max-w-md text-lg leading-relaxed text-slate-200/95">
                Our engine computes the cheapest container combination and the most space-efficient packing plan — simultaneously, <span className="font-black text-white">in under 2 seconds.</span>
              </p>
              <div className="mt-8 flex flex-col gap-3 text-sm text-slate-400">
                {["10 free simulations/month (no card)", "Used by Fortune 500 supply chain teams globally", "API available for platform integration"].map((t) => (
                  <div key={t} className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-[14px] text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                    {t}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right panel */}
          <div className="overflow-hidden rounded-3xl border border-white/10 bg-slate-950/70 shadow-2xl shadow-black/30 backdrop-blur-xl">
            <div className="p-8 md:p-10">
              {/* Mode tabs */}
              {!isForgot && (
                <div className="mb-6 flex rounded-xl border border-white/10 bg-white/5 p-1">
                  <button onClick={() => switchMode('login')} className={`flex-1 rounded-lg py-2 text-sm font-bold transition ${isLogin ? 'bg-primary text-white' : 'text-slate-400 hover:text-white'}`}>Sign in</button>
                  <button onClick={() => switchMode('signup')} className={`flex-1 rounded-lg py-2 text-sm font-bold transition ${isSignup ? 'bg-primary text-white' : 'text-slate-400 hover:text-white'}`}>Create account</button>
                </div>
              )}

              {/* Login flow */}
              {isLogin && step === 'credentials' && (
                <>
                  <h1 className="text-2xl font-black">Welcome back</h1>
                  <p className="mt-1 text-sm text-slate-400">Sign in to your Feuteuristix account.</p>
                  <LoginCredentialsForm onOTPSent={handleOTPSent} />
                  <button onClick={() => setMode('forgot')} className="mt-4 text-xs text-slate-500 hover:text-slate-300 transition">Forgot your password?</button>
                </>
              )}
              {isLogin && step === 'otp' && (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <StepDot active={false} done={true} label="1" />
                    <div className="h-px flex-1 bg-primary" />
                    <StepDot active={true} done={false} label="2" />
                  </div>
                  <h1 className="text-2xl font-black">Verify your email</h1>
                  <p className="mt-1 text-sm text-slate-400">Enter the 6-digit code sent to your inbox.</p>
                  <OTPForm email={email} onSuccess={handleSuccess} />
                  <button onClick={() => setStep('credentials')} className="mt-4 text-sm text-slate-400 hover:text-white transition">← Back</button>
                </>
              )}

              {/* Signup flow */}
              {isSignup && step === 'credentials' && (
                <>
                  <h1 className="text-2xl font-black">Create your account</h1>
                  <p className="mt-1 text-sm text-slate-400">Free · 10 simulations/month · No card required.</p>
                  <SignupForm onOTPSent={handleOTPSent} />
                </>
              )}
              {isSignup && step === 'otp' && (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <StepDot active={false} done={true} label="1" />
                    <div className="h-px flex-1 bg-primary" />
                    <StepDot active={true} done={false} label="2" />
                  </div>
                  <h1 className="text-2xl font-black">Verify your email</h1>
                  <p className="mt-1 text-sm text-slate-400">Confirm your email to activate your account.</p>
                  <OTPForm email={email} onSuccess={handleSuccess} />
                  <button onClick={() => setStep('credentials')} className="mt-4 text-sm text-slate-400 hover:text-white transition">← Back</button>
                </>
              )}

              {/* Forgot password */}
              {isForgot && (
                <>
                  <h1 className="text-2xl font-black">Reset password</h1>
                  <ForgotPasswordFlow onBack={() => switchMode('login')} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
